import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'
import styles from './Speedbump.module.scss'

interface SpeedbumpOptions {
  title: string
  /** Simple text message — rendered as a <p> automatically */
  message?: string
  /** Rich content — use for lists, mixed formatting, etc. */
  children?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmVariant?: 'primary' | 'danger'
}

type SpeedbumpFn = (options: SpeedbumpOptions) => Promise<boolean>

const SpeedbumpContext = createContext<SpeedbumpFn>(async () => false)

export const useSpeedbump = () => useContext(SpeedbumpContext)

export const SpeedbumpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<SpeedbumpOptions | null>(null)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<SpeedbumpFn>((opts) => {
    setOptions(opts)
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const handleClose = (result: boolean) => {
    setOptions(null)
    resolveRef.current?.(result)
    resolveRef.current = null
  }

  return (
    <SpeedbumpContext.Provider value={confirm}>
      {children}
      {options && (
        <Modal
          isOpen
          onClose={() => handleClose(false)}
          title={options.title}
          size="small"
          footer={
            <>
              <Button variant="secondary" size="small" onClick={() => handleClose(false)}>
                {options.cancelLabel || 'Cancel'}
              </Button>
              <Button variant={options.confirmVariant || 'primary'} size="small" onClick={() => handleClose(true)}>
                {options.confirmLabel || 'Confirm'}
              </Button>
            </>
          }
        >
          <div className={styles.body}>
            {options.message && <p>{options.message}</p>}
            {options.children}
          </div>
        </Modal>
      )}
    </SpeedbumpContext.Provider>
  )
}
